import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const marketingErrorTypeValues = [
  "META_GRAPH_REQUEST_REJECTED",
  "META_GRAPH_RATE_LIMITED",
  "META_GRAPH_FETCH_FAILED",
  "META_LEADGEN_NOT_CONFIGURED",
  "META_CAPI_NOT_CONFIGURED",
  "META_CAPI_EVENT_REJECTED",
  "META_CAPI_RATE_LIMITED",
  "META_CAPI_SEND_TRANSIENT_FAILURE",
  "LEAD_STAGE_EMIT_FAILED",
  "CONTRACT_SIGNED_COMPLETED_AT_MISSING",
  "CONTRACT_SIGNED_VALUE_UNRESOLVED",
] as const;
const marketingErrorTypeSchema = z.enum(marketingErrorTypeValues);
const MarketingErrorType = marketingErrorTypeSchema.enum;
type MarketingErrorType = z.infer<typeof marketingErrorTypeSchema>;

export type MarketingErrors =
  | {
      errorType: typeof MarketingErrorType.META_GRAPH_REQUEST_REJECTED;
      data: { metaLeadId: string; metaErrorCode: number; metaErrorSubcode: number | null };
    }
  | {
      errorType: typeof MarketingErrorType.META_GRAPH_RATE_LIMITED;
      data: { metaLeadId: string; metaErrorCode: number; retryAfterSeconds: number | null };
    }
  | {
      errorType: typeof MarketingErrorType.META_GRAPH_FETCH_FAILED;
      data: { metaLeadId: string; metaErrorCode: number | null; httpStatus: number | null };
    }
  | {
      errorType: typeof MarketingErrorType.META_LEADGEN_NOT_CONFIGURED;
      data: { metaLeadId: string };
    }
  | {
      errorType: typeof MarketingErrorType.META_CAPI_NOT_CONFIGURED;
      data: { marketingLeadStageId: string };
    }
  | {
      errorType: typeof MarketingErrorType.META_CAPI_EVENT_REJECTED;
      data: {
        marketingLeadId: string;
        marketingLeadStageId: string;
        metaErrorCode: number | null;
        metaErrorSubcode: number | null;
        httpStatus: number | null;
      };
    }
  | {
      errorType: typeof MarketingErrorType.META_CAPI_RATE_LIMITED;
      data: {
        marketingLeadId: string;
        marketingLeadStageId: string;
        metaErrorCode: number | null;
        retryAfterSeconds: number | null;
      };
    }
  | {
      errorType: typeof MarketingErrorType.META_CAPI_SEND_TRANSIENT_FAILURE;
      data: {
        marketingLeadId: string;
        marketingLeadStageId: string;
        metaErrorCode: number | null;
        httpStatus: number | null;
        transportErrorName: string | null;
      };
    }
  | {
      errorType: typeof MarketingErrorType.LEAD_STAGE_EMIT_FAILED;
      data: { marketingLeadStageId: string; originalErrorName: string; originalErrorMessage: string };
    }
  | {
      errorType: typeof MarketingErrorType.CONTRACT_SIGNED_COMPLETED_AT_MISSING;
      data: { marketingLeadId: string; documentId: string };
    }
  | {
      errorType: typeof MarketingErrorType.CONTRACT_SIGNED_VALUE_UNRESOLVED;
      data: {
        marketingLeadId: string;
        documentId: string;
        requestForQuoteId: number | null;
        quoteVersionId: string | null;
      };
    };

export const marketingErrorDetails: Record<MarketingErrorType, ErrorDetails> = {
  META_GRAPH_REQUEST_REJECTED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Meta Graph rejected the lead fetch with a non-retryable error code",
  },
  META_GRAPH_RATE_LIMITED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Meta Graph rate limited the lead fetch",
  },
  META_GRAPH_FETCH_FAILED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Meta Graph lead fetch failed with a retryable error",
  },
  META_LEADGEN_NOT_CONFIGURED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Meta leadgen fetch ran while the leadgen variables are not all configured",
  },
  META_CAPI_NOT_CONFIGURED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Meta Conversions API send ran in production while the CAPI variables are not configured",
  },
  META_CAPI_EVENT_REJECTED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Meta Conversions API rejected a stage event with a non-retryable error code",
  },
  META_CAPI_RATE_LIMITED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Meta Conversions API rate limited a stage event send",
  },
  META_CAPI_SEND_TRANSIENT_FAILURE: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Meta Conversions API stage event send failed with a retryable error",
  },
  LEAD_STAGE_EMIT_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "The marketing lead-stage Inngest event could not be emitted; the row is left PENDING for the sweep",
  },
  CONTRACT_SIGNED_COMPLETED_AT_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "The contract-signed marketing step ran before mark-document-completed stamped the document's completedAt",
  },
  CONTRACT_SIGNED_VALUE_UNRESOLVED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "The CONTRACT_SIGNED stage was recorded without a value because the signed quote version could not be resolved",
  },
};
